import { faChevronLeft, faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { CalendarAria } from '@react-aria/calendar';
import { useDateFormatter, useLocale } from '@react-aria/i18n';
import type { CalendarState, RangeCalendarState } from '@react-stately/calendar';
import type { CalendarPropsBase } from '@react-types/calendar';
import type { DOMProps, StyleProps } from '@react-types/shared';
import type { RefObject } from 'react';
import React, { useRef } from 'react';
import { ActionButton } from '../../../date-picker-button/action-button';
import { CalendarMonth } from '../calendar-month';

interface CalendarBaseProps<T extends CalendarState | RangeCalendarState>
  extends CalendarPropsBase,
    DOMProps,
    StyleProps {
  state: T;
  useCalendar: (props: CalendarPropsBase, state: T, ref?: RefObject<HTMLElement>) => CalendarAria;
  visibleMonths?: number;
}

export const CalendarBase = <T extends CalendarState | RangeCalendarState>(
  props: CalendarBaseProps<T>
) => {
  let { state, useCalendar, visibleMonths = 1 } = props;
  let { direction } = useLocale();
  let currentMonth = state.visibleRange.start;
  let monthDateFormatter = useDateFormatter({
    month: 'long',
    year: 'numeric',
    era: currentMonth.calendar.identifier !== 'gregory' ? 'long' : undefined,
    calendar: currentMonth.calendar.identifier
  });
  let ref = useRef(null);
  let { calendarProps, prevButtonProps, nextButtonProps } = useCalendar(props, state, ref);

  let titles = [];
  let calendars = [];
  for (let i = 0; i < visibleMonths; i++) {
    titles.push(
      <div key={i} className="monthHeader flex content-center items-center">
        {i === 0 && (
          <ActionButton
            {...prevButtonProps}
            isQuiet
            btnClassName=" hover:bg-blue-500 hover:text-white"
          >
            {direction === 'rtl' ? (
              <FontAwesomeIcon icon={faChevronRight} />
            ) : (
              <FontAwesomeIcon icon={faChevronLeft} />
            )}
          </ActionButton>
        )}
        <h2 className="calendar-title w-full text-center font-bold ">
          {monthDateFormatter.format(currentMonth.add({ months: i }).toDate(state.timeZone))}
        </h2>
        {i === visibleMonths - 1 && (
          <ActionButton
            {...nextButtonProps}
            isQuiet
            btnClassName=" hover:bg-blue-500 hover:text-white"
          >
            {direction === 'rtl' ? (
              <FontAwesomeIcon icon={faChevronLeft} />
            ) : (
              <FontAwesomeIcon icon={faChevronRight} />
            )}
          </ActionButton>
        )}
      </div>
    );

    let d = currentMonth.add({ months: i });
    calendars.push(
      <CalendarMonth {...props} key={`${d.year}-${d.month}-${d.day}`} state={state} startDate={d} />
    );
  }

  return (
    <div
      {...calendarProps}
      ref={ref}
      className="calendar-css border-blue-gray-500 rounded border shadow-xl"
    >
      <div className="calendar-header-css border-blue-gray-500 border-b">{titles}</div>
      <div className="calendar-months-css">{calendars}</div>
    </div>
  );
};
